import { useEffect } from 'react';

/**
 * SEO Helper Hook - Updates document meta tags dynamically
 * Helps with search engine optimization by setting page-specific metadata
 */
export const useSEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex = false
}) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = `${title} | SportsAnalysisAB`;
    }

    // Helper to update or create meta tag
    const updateMeta = (name, content, property = false) => {
      if (!content) return;
      
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Update description
    if (description) {
      updateMeta('description', description);
      updateMeta('og:description', description, true);
      updateMeta('twitter:description', description);
    }

    // Update keywords
    if (keywords) {
      updateMeta('keywords', keywords);
    }

    // Update canonical URL
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
      updateMeta('og:url', canonicalUrl, true);
      updateMeta('twitter:url', canonicalUrl);
    }

    // Update Open Graph image
    if (ogImage) {
      updateMeta('og:image', ogImage, true);
      updateMeta('twitter:image', ogImage);
    }

    // Update OG type
    updateMeta('og:type', ogType, true);

    // Update OG title
    if (title) {
      updateMeta('og:title', `${title} | SportsAnalysisAB`, true);
      updateMeta('twitter:title', `${title} | SportsAnalysisAB`);
    }

    // Handle noIndex
    if (noIndex) {
      updateMeta('robots', 'noindex, nofollow');
    }

    // Cleanup function - reset to defaults when component unmounts
    return () => {
      // We don't remove the meta tags, just let the next page update them
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noIndex]);
};

/**
 * SEO Keywords by page type - for dynamic keyword optimization
 */
export const SEO_KEYWORDS = {
  home: 'sports predictions, sports betting analysis, who will win tonight, NHL predictions, NBA predictions, NFL predictions, MLB predictions, sports analysis, Alberta sports betting, Canadian sports picks, AI sports predictions, free sports picks, expert picks',
  
  board: 'todays games, live sports predictions, sports picks today, who will win, game predictions, betting picks, sports odds, live scores',
  
  nhl: 'NHL predictions, hockey predictions, who will win NHL tonight, hockey picks, NHL betting, NHL analysis, hockey odds',
  
  nba: 'NBA predictions, basketball predictions, who will win NBA tonight, basketball picks, NBA betting, NBA analysis, basketball odds',
  
  nfl: 'NFL predictions, football predictions, who will win NFL, football picks, NFL betting, NFL analysis, football odds',
  
  mlb: 'MLB predictions, baseball predictions, who will win MLB, baseball picks, MLB betting, MLB analysis, baseball odds',
  
  accuracy: 'sports prediction accuracy, prediction results, sports picks track record, betting accuracy, prediction success rate',
  
  playerStats: 'player statistics, sports stats, NHL stats, NBA stats, player analysis, sports leaders, top scorers',
  
  gameDetail: 'game prediction, game analysis, who will win, matchup analysis, head to head, injury report, game preview'
};

/**
 * Generate dynamic meta description based on game data
 */
export const generateGameDescription = (game) => {
  if (!game) return '';
  
  const homeTeam = game.home_team?.name || 'Home Team';
  const awayTeam = game.away_team?.name || 'Away Team';
  const homeProb = game.home_probability?.toFixed(0) || '50';
  const awayProb = game.away_probability?.toFixed(0) || '50';
  const league = game.league_id?.toUpperCase() || 'SPORTS';
  
  return `${awayTeam} vs ${homeTeam} prediction: Our AI analysis gives ${homeTeam} a ${homeProb}% chance to win. Get detailed ${league} predictions, player matchups, and injury reports.`;
};

/**
 * Generate page-specific title
 */
export const generatePageTitle = (pageType, additionalInfo = '') => {
  const titles = {
    home: 'Sports Predictions & Analysis',
    board: `Today's Game Predictions`,
    accuracy: 'Prediction Accuracy & Track Record',
    playerStats: 'Player Statistics & Leaders',
    gameDetail: additionalInfo || 'Game Prediction & Analysis',
    login: 'Sign In',
    signup: 'Start Free Trial',
    billing: 'Pricing & Subscription',
    methodology: 'Our Prediction Methodology'
  };
  
  return titles[pageType] || 'Sports Analysis';
};

export default useSEO;
